import { useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { generateDispatchBatchId } from '../utils/dispatchBatchId';
import type {
  AuthType,
  ButtonSpec,
  ExternalLinkSendPayload,
  ExternalLinkSendResult,
  Recipient,
  SaveDialogItem,
  SendResultItem,
} from '../types';

interface ExternalLinkOpts {
  sourceService: string;
  sourceAgent: string;
  resourceType: string;
  shareType: string;
  shareScope: string;
  authType: AuthType;
  /**
   * 발송 전송을 도메인별로 넣는 필수 주입점. 각 도메인 api(예: sv-photo sendPhotoShare,
   * sv-schedule sendScheduleShare)가 직접 오케스트레이션한다(W5b·W6b).
   * payload·반환 형태는 도메인별 발송 함수가 동일하게 맞춘다.
   */
  sendOverride: (payload: ExternalLinkSendPayload) => Promise<ExternalLinkSendResult>;
}

type ApiRecipient = ExternalLinkSendPayload['recipients'][number];

// 담당 공종이 있는 반장·거래처는 공종마다 수신자 행을 따로 만든다(스케줄 공유 — 공종이 다르면 링크도 별개).
function toApiRecipients(r: Recipient): ApiRecipient[] {
  const base = {
    name: r.name,
    mobile: r.mobile.replace(/-/g, ''), // API는 하이픈 없는 순수 숫자 포맷
    recipientType: r.recipientType,
  };
  if (r.workTypes && r.id != null) {
    return r.workTypes.map((w) => ({ ...base, partnerId: r.id, workTypeCode: w.code }));
  }
  return [base];
}

export function useExternalLink(opts: ExternalLinkOpts) {
  const [isSending, setIsSending] = useState(false);
  const [saveDialogItems, setSaveDialogItems] = useState<SaveDialogItem[]>([]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Always-current opts reference — avoids stale closure without deps churn
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const notifyResult = useCallback((recipients: Recipient[], result: ExternalLinkSendResult) => {
    const newOnes = recipients.filter((r) => r.isNew);
    if (newOnes.length > 0) {
      setSaveDialogItems(
        newOnes.map((r) => ({
          key: r.key,
          name: r.name,
          mobile: r.mobile,
          recipientType: r.recipientType,
          excluded: false,
          specialtyTypes: [],
          vendorName: '',
          dealItems: [],
        })),
      );
      setSaveDialogOpen(true);
    } else if (result.failedCount > 0) {
      toast.error(`${result.failedCount}명 발송 실패`);
    } else {
      toast.success(`${result.sentCount}명에게 발송 완료`);
    }
  }, []);

  const send = useCallback(
    async (
      resourceRef: string,
      recipients: Recipient[],
      variables: Record<string, string>,
      expiresAt: string,
      dispatchBatchId?: string,
      buttons?: ButtonSpec[],
      resolveRecipientVariables?: (recipient: Recipient, baseVariables: Record<string, string>) => Record<string, string>,
    ): Promise<{ result: ExternalLinkSendResult; payload: ExternalLinkSendPayload } | null> => {
      setIsSending(true);
      try {
        const { sourceService, sourceAgent, resourceType, shareType, shareScope, authType } =
          optsRef.current;
        const basePayload = {
          sourceService, sourceAgent, resourceType, resourceRef, shareType, shareScope, authType, expiresAt,
          ...(buttons && buttons.length > 0 && { buttons }),
        };

        // 수신자별 치환 변수(예: #{recipient_name} 포함 본문)가 없으면 기존처럼 한 번에 전체 발송.
        if (!resolveRecipientVariables) {
          const payload: ExternalLinkSendPayload = {
            ...basePayload,
            recipients: recipients.flatMap(toApiRecipients),
            variables,
            ...(dispatchBatchId && { dispatchBatchId }),
          };
          const result = await optsRef.current.sendOverride(payload);
          notifyResult(recipients, result);
          return { result, payload };
        }

        // 수신자마다 본문이 달라져야 하면 1명씩 나눠 호출하고, 같은 dispatchBatchId로 묶는다.
        const batchId = dispatchBatchId ?? generateDispatchBatchId();
        const items: SendResultItem[] = [];
        let shareBatchId: string | undefined;
        let channel: string | undefined;
        for (const r of recipients) {
          const payload: ExternalLinkSendPayload = {
            ...basePayload,
            recipients: toApiRecipients(r),
            variables: { ...variables, ...resolveRecipientVariables(r, variables) },
            dispatchBatchId: batchId,
          };
          const res = await optsRef.current.sendOverride(payload);
          items.push(...res.results);
          shareBatchId ??= res.shareBatchId;
          channel ??= res.channel;
        }
        const result: ExternalLinkSendResult = {
          shareBatchId,
          dispatchBatchId: batchId,
          channel,
          sentCount: items.filter((it) => it.success).length,
          failedCount: items.filter((it) => !it.success).length,
          results: items,
        };
        const payload: ExternalLinkSendPayload = {
          ...basePayload,
          recipients: recipients.flatMap(toApiRecipients),
          variables,
          dispatchBatchId: batchId,
        };
        notifyResult(recipients, result);
        return { result, payload };
      } catch (e) {
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '발송에 실패했습니다.';
        toast.error(msg);
        return null;
      } finally {
        setIsSending(false);
      }
    },
    [notifyResult],
  );

  return {
    isSending,
    send,
    saveDialogItems,
    setSaveDialogItems,
    saveDialogOpen,
    setSaveDialogOpen,
  };
}
